import {
	IShopReward,
	IShopRewardItem,
	IShopStore
} from "contexts/WebStoreProvider/types";
import { Node, Tracker } from "interfaces/drip-offer";

export const getAvailablePrizes = (nodes: Node[]) => {
	return nodes.filter(
		(n) => n.state === "unavailable" || n.state === "available"
	).length;
};

export const handleTicketVariant = (url: string, isClaimable: boolean) => {
	return url.replace(
		"{drip_offer_availability}",
		isClaimable ? "available" : "unavailable"
	);
};

export const handleTitleImageVariant = (url: string, isBought: boolean) => {
	return url?.replace("{drip_offer_type}", isBought ? "reward" : "offer");
};

export const getMissedDays = (nodes: Node[], localTime: string) => {
	let unclaimedDays = 0;
	const currentDate = new Date(localTime);

	nodes.forEach((node) => {
		const { state, claimedDate, claimAvailableDate } = node;

		if (state === "claimed") {
			const claimedDateObj = new Date(claimedDate);
			const claimAvailableDateObj = new Date(claimAvailableDate);
			const daysDiff = Math.ceil(
				(claimedDateObj.valueOf() - claimAvailableDateObj.valueOf()) /
					(1000 * 60 * 60 * 24)
			);
			if (daysDiff > 1) {
				unclaimedDays += daysDiff - 1;
			}
		} else if (state === "available") {
			const availableDate = new Date(claimAvailableDate);
			const daysDiff = Math.ceil(
				(currentDate.valueOf() - availableDate.valueOf()) /
					(1000 * 60 * 60 * 24)
			);
			if (daysDiff > 1) {
				unclaimedDays += daysDiff - 1;
			}
		}
	});

	return unclaimedDays;
};

export const getNodeItemData = (store: IShopStore, node: Node) => {
	const reward = store.rewards[node.rewardId];
	if (!reward) {
		console.error("reward", reward);
		return null;
	}
	const items: IShopRewardItem[] = Object.keys(reward.items).map((key) => {
		const { imageUrl = "" } = {
			...store.rewardTypes.filter((t: IShopReward) => t.id === key)[0]
		};
		const rewardItem = reward.items;
		return {
			id: key,
			value: rewardItem[key as keyof typeof rewardItem],
			imageUrl
		};
	});

	return items;
};

export const getFilteredTracker = (
	trackers: Tracker[]
): Tracker | undefined => {
	const trackersWithPurchaseDateAndAvailableNode = trackers.filter(
		(t) =>
			!!t.offerPurchaseDate &&
			Array.isArray(t.nodes) &&
			t.nodes.some((node) => node.state === "available")
	);

	switch (trackersWithPurchaseDateAndAvailableNode.length) {
		case 0:
			const trackersWithoutPurchaseDate = trackers.filter(
				(t) => !t.offerPurchaseDate
			);

			switch (trackersWithoutPurchaseDate.length) {
				case 0:
					return undefined;

				case 1:
					return trackersWithoutPurchaseDate[0];

				default:
					return trackersWithoutPurchaseDate.reduce((prev, curr) => {
						const prevDate = new Date(prev.claimEndDate as string).getTime();
						const currDate = new Date(curr.claimEndDate as string).getTime();
						return prevDate < currDate ? prev : curr;
					});
			}

		case 1:
			return trackersWithPurchaseDateAndAvailableNode[0];

		default:
			// If there are several, filter by the closest (most recent) date
			return trackersWithPurchaseDateAndAvailableNode.reduce((prev, curr) => {
				const prevDate = new Date(prev.offerPurchaseDate as string).getTime();
				const currDate = new Date(curr.offerPurchaseDate as string).getTime();
				return prevDate < currDate ? prev : curr;
			});
	}
};

export const sortTrackersByIndex = (trackers: Tracker[]): number[] => {
	if (!Array.isArray(trackers)) {
		return [];
	}

	// 1. Trackers with offerPurchaseDate y node.state === "available"
	const withPurchaseDateAndAvailable = trackers
		.map((t, idx) => ({ ...t, _idx: idx }))
		.filter(
			(t) =>
				!!t.offerPurchaseDate &&
				Array.isArray(t.nodes) &&
				t.nodes.some((node) => node.state === "available")
		);

	// 2. Trackers with offerPurchaseDate but node.state !== "available"
	const withPurchaseDateAndNotAvailable = trackers
		.map((t, idx) => ({ ...t, _idx: idx }))
		.filter(
			(t) =>
				!!t.offerPurchaseDate &&
				Array.isArray(t.nodes) &&
				!t.nodes.some((node) => node.state === "available")
		);

	// 3. Trackers without offerPurchaseDate
	const withoutPurchaseDate = trackers
		.map((t, idx) => ({ ...t, _idx: idx }))
		.filter((t) => !t.offerPurchaseDate);

	// Case 1: Trackes without offerPurchaseDate, sort by offerEndDate
	if (
		withPurchaseDateAndAvailable.length === 0 &&
		withPurchaseDateAndNotAvailable.length === 0
	) {
		return [...withoutPurchaseDate]
			.sort((a, b) => {
				const aDate = new Date(a.offerEndDate as string).getTime();
				const bDate = new Date(b.offerEndDate as string).getTime();
				return aDate - bDate;
			})
			.map((t) => t._idx);
	}

	// Case 2: some trackers have offerPurchaseDate and node.state === "available"
	const sortedWithPurchaseDateAndAvailable = [
		...withPurchaseDateAndAvailable
	].sort((a, b) => {
		const aDate = new Date(a.claimEndDate as string).getTime();
		const bDate = new Date(b.claimEndDate as string).getTime();
		return aDate - bDate;
	});

	const sortedWithoutPurchaseDate = [...withoutPurchaseDate].sort((a, b) => {
		const aDate = new Date(a.offerEndDate as string).getTime();
		const bDate = new Date(b.offerEndDate as string).getTime();
		return aDate - bDate;
	});

	// Case 3: All trackers have offerPurchaseDate but node.state !== "available"
	if (
		withPurchaseDateAndAvailable.length === 0 &&
		withPurchaseDateAndNotAvailable.length > 0 &&
		withoutPurchaseDate.length === 0
	) {
		return [...withPurchaseDateAndNotAvailable]
			.sort((a, b) => {
				const aDate = new Date(a.claimEndDate as string).getTime();
				const bDate = new Date(b.claimEndDate as string).getTime();
				return aDate - bDate;
			})
			.map((t) => t._idx);
	}

	// Mixed: first all with offerPurchaseDate and node.state === "available" (sorted by claimEndDate), then without offerPurchaseDate (sorted by offerEndDate), then offerPurchaseDate and node.state !== "available" (sorted by claimEndDate)
	const sortedWithPurchaseDateAndNotAvailable = [
		...withPurchaseDateAndNotAvailable
	].sort((a, b) => {
		const aDate = new Date(a.claimEndDate as string).getTime();
		const bDate = new Date(b.claimEndDate as string).getTime();
		return aDate - bDate;
	});

	return [
		...sortedWithPurchaseDateAndAvailable,
		...sortedWithoutPurchaseDate,
		...sortedWithPurchaseDateAndNotAvailable
	].map((t) => t._idx);
};
